import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetPasswordWithToken } from '../lib/sqlAuth';
import '../csscomponent/AuthAssist.css';
import useSingleTapNavigate from '../lib/useSingleTapNavigate';
import LogoButtonLoader from './mobile/LogoButtonLoader';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { goTo, navLocked } = useSingleTapNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);
  const redirectTimerRef = useRef(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const getPasswordError = (value) => {
    if (!value) return 'Enter and confirm your new password.';
    if (value.length < 8 || !/[A-Z]/.test(value) || !/[a-z]/.test(value) || !/[0-9]/.test(value)) {
      return 'Use 8+ characters with uppercase, lowercase, and a number.';
    }
    return '';
  };

  useEffect(() => () => {
    if (redirectTimerRef.current) {
      window.clearTimeout(redirectTimerRef.current);
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setMessage('Reset token is missing from URL.');
      return;
    }
    if (!password || !confirmPassword) {
      setMessage('Enter and confirm your new password.');
      return;
    }
    const passwordError = getPasswordError(password);
    if (passwordError) {
      setMessage(passwordError);
      return;
    }
    if (password !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }

    try {
      setLoading(true);
      const result = await resetPasswordWithToken({ token, password });
      setMessage(result?.message || 'Password updated successfully.');
      redirectTimerRef.current = window.setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1200);
    } catch (error) {
      setMessage(error?.message || 'Unable to reset password right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-assist-page">
      <div className="auth-assist-card">
        <div className="auth-assist-topbar">
          <button
            type="button"
            className="auth-assist-back"
            onClick={() => goTo('/login')}
            disabled={navLocked}
          >
            Back to Login
          </button>
          <button
            type="button"
            className="auth-page-skip auth-assist-skip"
            onClick={() => goTo('/home')}
            disabled={navLocked}
          >
            Skip
          </button>
        </div>
        <h1>Reset Password</h1>
        <p>Create a new password for your account.</p>
        <form onSubmit={onSubmit} className="auth-assist-form">
          <label htmlFor="new-password">New Password</label>
          <input
            id="new-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Minimum 8 chars with A-Z, a-z, 0-9"
            required
          />
          <label htmlFor="confirm-password">Confirm Password</label>
          <input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter password"
            required
          />
          <button type="submit" disabled={loading}>
            {loading ? (
              <LogoButtonLoader text="Updating password..." />
            ) : (
              'Update password'
            )}
          </button>
        </form>
        {message ? <p className="auth-assist-message">{message}</p> : null}
      </div>
    </div>
  );
}
